import { useState } from "react";
import { Download, Loader2, AlertCircle } from "lucide-react";
import { getBaseUrl, getToken } from "../../lib/storage";

interface FileMention {
  fileId: string;
  name?: string;
}

/**
 * Извлекает упоминания file_id из текста ответа агента.
 *
 * Поддерживает три паттерна, которые агент использует чаще всего:
 *  1. `📎 <имя> (file_id: <id>)`         — вложение / результат генерации
 *  2. `file_id: <id>`                    — голый id-маркер
 *  3. `/api/v1/files/<id>/download`      — прямая ссылка из markdown
 *
 * Дубли по fileId схлопываются (берём первый встретившийся name).
 */
export function extractFileMentions(text: string): FileMention[] {
  const mentions = new Map<string, string | undefined>();

  const re1 = /📎\s+([^\n()]+?)\s+\(\s*file_id\s*:\s*([\w-]+)\s*\)/gi;
  const re2 = /\bfile_id\s*[:=]\s*([\w-]{6,})\b/gi;
  const re3 = /\/api\/v1\/files\/([\w-]{6,})\/(?:content|download)/gi;

  let m: RegExpExecArray | null;
  while ((m = re1.exec(text)) !== null) {
    const id = m[2];
    if (!mentions.has(id)) mentions.set(id, m[1].trim());
  }
  while ((m = re2.exec(text)) !== null) {
    const id = m[1];
    if (!mentions.has(id)) mentions.set(id, undefined);
  }
  while ((m = re3.exec(text)) !== null) {
    const id = m[1];
    if (!mentions.has(id)) mentions.set(id, undefined);
  }

  return [...mentions.entries()].map(([fileId, name]) => ({ fileId, name }));
}

/**
 * Скачивает файл через `/api/v1/files/{id}/download` с Bearer-токеном.
 * Использует blob → temporary <a download> чтобы запустить нативный save-dialog
 * браузера/Tauri (прямой <a href> с auth не работает — браузер не добавит заголовки).
 */
async function downloadAuthorizedFile(fileId: string, fallbackName?: string): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const r = await fetch(`${getBaseUrl()}/api/v1/files/${encodeURIComponent(fileId)}/content`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!r.ok) {
      let detail = "";
      try {
        const j = await r.json();
        detail = (j as Record<string, unknown>).detail as string || "";
      } catch {
        detail = await r.text().catch(() => "");
      }
      return { ok: false, error: `HTTP ${r.status}${detail ? `: ${detail.slice(0, 200)}` : ""}` };
    }
    const disposition = r.headers.get("content-disposition") || "";
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const finalName = (match ? decodeURIComponent(match[1]) : fallbackName) || `file-${fileId}`;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Сетевая ошибка" };
  }
}

/**
 * Карточки скачивания. Рендерится после текста ответа агента.
 * Если в тексте нет упоминаний файлов — null (нет лишних пустых блоков).
 */
export function FileDownloadCards({ text }: { text: string }) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const mentions = extractFileMentions(text);
  if (mentions.length === 0) return null;

  return (
    <div className="mt-3 space-y-1.5">
      {mentions.map(({ fileId, name }) => {
        const err = errors[fileId];
        return (
          <div key={fileId}>
            <button
              type="button"
              onClick={async () => {
                setBusyId(fileId);
                setErrors((e) => {
                  const c = { ...e };
                  delete c[fileId];
                  return c;
                });
                const res = await downloadAuthorizedFile(fileId, name);
                setBusyId((b) => (b === fileId ? null : b));
                if (!res.ok) {
                  setErrors((e) => ({ ...e, [fileId]: res.error }));
                }
              }}
              disabled={busyId === fileId}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-card border border-accent-500/40 bg-accent-500/5 hover:bg-accent-500/15 disabled:opacity-60 text-small text-fg transition-colors text-left"
              title={`Скачать файл (id: ${fileId})`}
            >
              {busyId === fileId ? (
                <Loader2 className="size-4 animate-spin shrink-0 text-accent-400" />
              ) : (
                <Download className="size-4 shrink-0 text-accent-400" />
              )}
              <span className="font-medium truncate">
                {name || `Скачать файл`}
              </span>
              <span className="text-fg-subtle text-[0.78em] font-mono ml-auto shrink-0">
                {fileId.length > 12 ? `${fileId.slice(0, 8)}…` : fileId}
              </span>
            </button>
            {err && (
              <div className="mt-1 flex items-start gap-1.5 px-2 text-[0.78rem] text-yellow-400">
                <AlertCircle className="size-3.5 mt-0.5 shrink-0" />
                <span>{err}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
